import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Tag } from "antd";
import { useExportIntegrationStepLogsMutation, useGetIntegrationStepLogsQuery } from "api/TenantSlug/StepLog/hooks";
import { IntegrationStepLogDetailDrawer } from "components/Drawers/IntegrationStepLogDetailDrawer/IntegrationStepLogDetailDrawer";
import { useStepTypeLabel } from "components/UI/Selects/StepTypeSelectSearchable/useStepTypeLabel.ts";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
// import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import GetStepLogIntegrationsResponse = App.Http.Responses.StepLog.GetStepLogIntegrationsResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface SearchParams {
    [`integrationStepLogDetail-id`]: string;
}

export const CONVERSION_STATE_EID_FILTER_NAME = "eid";

export const IntegrationStepLogTable = () => {
    const tableId = TableId.IntegrationStepLogTable;
    const { t } = usePaldockTranslation("tables.integration-step-log");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");

    const { t: tCC } = usePaldockTranslation("components.connection-creator");
    const getStepTypeLabel = useStepTypeLabel();
    const tableAtomInstance = tableAtom<App.Data.StepLogIntegrationDataOptional>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    // const exportState = useRecoilValue(exportAtom(tableId));
    const permissionsUtils = usePermission();
    const createdAtStr = useCreatedAtFilterValue();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(tableState.filter?.filter ?? {}),
                ...("started_at" in (tableState.filter?.filter ?? {})
                    ? {}
                    : {
                          started_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? "-started_at",
            include: "offer,cascade,owner,advertiser,stepRun",
            fields: {
                step_log_integrations:
                    "id,origin_eid,eid,owner_id,advertiser_id,offer_id,cascade_id,step_run_id,integration_id,integration_name,meaning,http_status_code,state,message,created_at,started_at",
                owner: "id,nickname",
                advertiser: "id,nickname",
                offer: "id,title",
                cascade: "id,title",
                stepRun: "title,type",
            },
        },
    };

    const getIntegrationLogsQuery = useGetIntegrationStepLogsQuery(request, { enabled: false });
    const getIntegrationLogsExportMutation = useExportIntegrationStepLogsMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetStepLogIntegrationsResponse,
        void,
        App.Data.StepLogIntegrationDataOptional,
        BaseExportRequest
    >(tableId, getIntegrationLogsQuery, undefined, undefined, getIntegrationLogsExportMutation);

    // TODO: view scenario run - commented out for now
    // const eidFilter = tableState.filter?.filter?.[CONVERSION_STATE_EID_FILTER_NAME] as
    //     | Record<string, unknown>
    //     | undefined;
    // const isEidEqualFilterActive = !!eidFilter?.eq;
    //
    // const logs = tableFetchDataUtils.tableData?.integrationLogs ?? [];
    // const sharedScenarioRunId = (() => {
    //     if (!isEidEqualFilterActive || logs.length === 0) return null;
    //     const firstId = logs[0]?.stepRun?.scenario_run_id;
    //     if (!firstId) return null;
    //     const allSame = logs.every((log) => log.stepRun?.scenario_run_id === firstId);
    //     return allSame ? firstId : null;
    // })();
    //
    // const viewScenarioRunButton = isEidEqualFilterActive ? (
    //     sharedScenarioRunId ? (
    //         <TenantLink to={`/scenario/history/${sharedScenarioRunId}`}>
    //             <Button icon={<NodeIndexOutlined />}>{tGA("view_scenario_run")}</Button>
    //         </TenantLink>
    //     ) : (
    //         <Button icon={<NodeIndexOutlined />} disabled>
    //             {tGA("view_scenario_run")}
    //         </Button>
    //     )
    // ) : null;

    const { getRowHandlers, getRowClassName } = useRowHandlers<App.Data.StepLogIntegrationDataOptional>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.IntegrationStepLogDetail,
                drawerParam: `${DrawerId.IntegrationStepLogDetail}-id`,
                permission: PermissionEnum.StepLogIntegrationsView,
            },
        ],
    });

    const columnConfig: TableColumnType<App.Data.StepLogIntegrationDataOptional>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.started")} />,
            dataIndex: "started_at",
            key: "started_at",
            render: (_, row) => <DateTimeCell value={row.started_at ?? undefined} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("created_at")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            ellipsis: true,
            width: 150,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "origin_eid",
            key: "origin_eid",
            render: (_, row) => (row.origin_eid ? <ShortenedIdWithCopy id={row.origin_eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "origin_eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "eid",
            key: "eid",
            render: (_, row) => (row.eid ? <ShortenedIdWithCopy id={row.eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("run_id")} />,
            dataIndex: "step_run_id",
            key: "step_run_id",
            render: (_, row) => (row.step_run_id ? <ShortenedIdWithCopy id={row.step_run_id} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "step_run_id",
            ellipsis: true,
            width: 150,
            visible: false,
        },
        TableColumn.Entity.Owner<App.Data.StepLogIntegrationDataOptional>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            width: 150,
            getEntity: (row) => (row.owner && row.owner_id ? { ...row.owner, id: row.owner_id } : undefined),
        }),
        TableColumn.Entity.Advertiser<App.Data.StepLogIntegrationDataOptional>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            dataIndex: "advertiser.nickname",
            key: "advertiser.nickname",
            filterType: FILTER_TYPES.advertiserSelect,
            getEntity: (row) =>
                row.advertiser && row.advertiser_id ? { ...row.advertiser, id: row.advertiser_id } : undefined,
            ellipsis: true,
            width: 150,
        }),
        TableColumn.Entity.Offer<App.Data.StepLogIntegrationDataOptional>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            ellipsis: true,
            width: 150,
            getEntity: (row) => (row.offer && row.offer_id ? { ...row.offer, id: row.offer_id } : undefined),
        }),
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.channel")} />,
            dataIndex: "cascade.title",
            key: "cascade.title",
            render: (_, row) => (
                <EntityLinkCell.Cascade
                    entity={row.cascade && row.offer_id ? { ...row.cascade, offer_id: row.offer_id } : undefined}
                />
            ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "cascade.title",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.integration")} />,
            dataIndex: "integration_name",
            key: "integration_name",
            render: (_, row) =>
                row.integration_id && row.integration_name ? (
                    <EntityLinkCell.Integration
                        entity={{ id: row.integration_id, name: row.integration_name } as App.Data.IntegrationData}
                    />
                ) : (
                    <EmptyCell />
                ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "integration_name",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.flow")} />,
            dataIndex: "meaning",
            key: "meaning",
            render: (_, row) => {
                const meaningToFlowType: Record<string, string> = {
                    check: "ping",
                    send: "post",
                    postsend_verification: "postVerify",
                };
                const meaning = (row as unknown as { meaning?: string }).meaning;
                if (!meaning) return <EmptyCell />;
                return <span>{tCC(`enums.flow_types.${meaningToFlowType[meaning] ?? meaning}`)}</span>;
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "meaning",
            filterOptions: [
                { label: tCC("enums.flow_types.ping"), value: "check" },
                { label: tCC("enums.flow_types.post"), value: "send" },
                { label: tCC("enums.flow_types.postVerify"), value: "postsend_verification" },
            ],
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.step")} />,
            dataIndex: "stepRun.title",
            key: "stepRun.title",
            render: (_, row) => row.stepRun?.title || <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "stepRun.title",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.type")} />,
            dataIndex: "stepRun.type",
            key: "stepRun.type",
            render: (_, row) => {
                const type = row.stepRun?.type;
                if (!type) return <EmptyCell />;
                if (type === "integration") return getStepTypeLabel(type).split(" - ")[0];
                return getStepTypeLabel(type);
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.integrationStepTypeSelect,
            filterName: "stepRun.type",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("status")} />,
            dataIndex: "http_status_code",
            key: "http_status_code",
            render: (_, row) => row.http_status_code ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "http_status_code",
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "state",
            key: "state",
            render: (_, row) => {
                const isOk = row.state === true;
                return (
                    <Tag icon={isOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={isOk ? "green" : "red"}>
                        {isOk ? tGL("ok") : tGL("fail")}
                    </Tag>
                );
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "state",
            filterOptions: [
                { label: tGL("ok"), value: "true" },
                { label: tGL("fail"), value: "false" },
            ],
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
            dataIndex: "message",
            key: "message",
            render: (message, row) => (
                <StepLogMessageCell message={message} stepType={row.stepRun?.type} id={`message-${row.id}`} />
            ),
            width: 390,
            ellipsis: true,
        },
    ];

    return (
        <>
            <Table<GetStepLogIntegrationsResponse, App.Data.StepLogIntegrationDataOptional>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetStepLogIntegrationsResponse}
                dataSource={tableFetchDataUtils.tableData?.integrationLogs ?? []}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                // onExport={() =>
                //     tableFetchDataUtils.exportData({
                //         filter: request.filter,
                //         extension: exportState.extension,
                //         headers: exportState.headers,
                //         page: request.page,
                //         export_all: exportState.range === "all",
                //         delimiter: exportState.delimiter,
                //     })
                // }
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"started_at"}
                dateFilterMode={"minute"}
                autoScrollWidth={true}
                hideActionsColumn={true}
            />

            {permissionsUtils.hasPermission(PermissionEnum.StepLogIntegrationsView) &&
                searchParams[`${DrawerId.IntegrationStepLogDetail}-id`] && (
                    <IntegrationStepLogDetailDrawer logId={searchParams[`${DrawerId.IntegrationStepLogDetail}-id`]} />
                )}
        </>
    );
};
